<template>
  <CCard class="project-prerequisite-card">
    <CCardHeader>
      <div class="project-prerequisite-heading">
        <p class="project-prerequisite-heading__eyebrow">PROJECT SETUP REQUIRED</p>
        <h2>プロジェクト設定が必要です</h2>
      </div>
    </CCardHeader>
    <CCardBody>
      <div class="alert alert-warning" role="alert" aria-live="polite">
        <strong>{{ targetMode ? '対象のスタッフは現在この画面を利用できません。' : 'この画面は現在利用できません。' }}</strong>
        <p class="mb-0 mt-2">{{ guidanceMessage }}</p>
      </div>

      <div class="project-prerequisite-steps">
        <h3>利用開始までの手順</h3>
        <ol>
          <li>{{ setupInstruction }}</li>
          <li>設定完了後、この画面の「設定を再確認」を押します。</li>
          <li>設定済みであれば、元の勤務表または業務報告へ戻ります。</li>
        </ol>
      </div>

      <!--
        ［設定を再確認］の結果。まだ設定されていないときも何か言う
        （ボタンの文字が一瞬「確認中...」になるだけで、押しても何も起きないように見えた）。
      -->
      <ActionResultMessage :text="result.text" :type="result.type" />

      <div class="project-prerequisite-actions">
        <CButton
          color="primary"
          :disabled="loading"
          @click="retry"
        >
          {{ loading ? '確認中...' : '設定を再確認' }}
        </CButton>
        <router-link
          v-if="!targetMode && canManageProject"
          class="btn btn-outline-primary"
          to="/project/list"
        >
          自分の所属プロジェクトを設定する
        </router-link>
        <router-link
          v-if="targetMode && canManageOtherMembership"
          class="btn btn-outline-primary"
          to="/project/list"
        >
          社員別の所属管理を開く
        </router-link>
        <router-link class="btn btn-secondary" to="/dashboard">
          ダッシュボードへ戻る
        </router-link>
      </div>

      <p v-if="targetMode" class="project-prerequisite-note">
        通常は対象スタッフ本人がログインし、自分の所属先を選択します。管理者によるスタッフ別設定は補助手段であり、スタッフ本人の設定機能を置き換えるものではありません。
      </p>
      <p v-else-if="!canManageProject" class="project-prerequisite-note">
        自分の所属プロジェクト設定画面を開けない場合は、契約企業管理者へ権限グループ・権限の確認を依頼してください。
      </p>
    </CCardBody>
  </CCard>
</template>

<script>
import { getAccessContext } from '../../services/accessContext'
import {
  canManageOtherUserProjectMembership,
  canManageProjectConfiguration,
  getProjectPrerequisite,
  projectPrerequisiteMessage,
  safeProjectReturnPath
} from '../../services/projectPrerequisite'
import { resolveRouteValue } from '../../common/routeRecordId'
import ActionResultMessage from '../../components/ActionResultMessage.vue'

export default {
  name: 'ProjectPrerequisiteRequired',

  components: { ActionResultMessage },

  data () {
    return {
      loading: false,
      // ［設定を再確認］の結果。ボタンのすぐ上に出す。
      result: { type: 'info', text: '' },
      canManageProject: false,
      canManageOtherMembership: false,
      reasonCode: String(this.$route.query.reason || 'NO_AFFILIATION')
    }
  },

  computed: {
    targetMode () {
      return this.$route.query.scope === 'target'
    },

    targetUserId () {
      if (!this.targetMode) return null
      return resolveRouteValue(this.$route, 'targetRef', 'USER') ||
        resolveRouteValue(this.$route, 'targetUserId', 'USER')
    },

    returnPath () {
      return safeProjectReturnPath(this.$route.query.from)
    },

    guidanceMessage () {
      return projectPrerequisiteMessage(this.reasonCode, this.targetMode)
    },

    setupInstruction () {
      return this.targetMode
        ? '対象スタッフ本人がログインし、プロジェクト画面で自分の所属先を選択します。'
        : '「自分の所属プロジェクトを設定する」から、自分の所属先を選択します。'
    }
  },

  created () {
    getAccessContext()
      .then(context => {
        this.canManageProject = canManageProjectConfiguration(context)
        this.canManageOtherMembership = canManageOtherUserProjectMembership(context)
      })
      .catch(() => {
        this.canManageProject = false
        this.canManageOtherMembership = false
      })
  },

  methods: {
    retry () {
      if (this.targetMode && this.targetUserId == null) {
        this.result = { type: 'danger', text: '対象スタッフを確認できません。勤務表または業務報告の一覧から選び直してください。' }
        return
      }

      this.loading = true
      this.result = { type: 'info', text: '' }
      getProjectPrerequisite({ targetUserId: this.targetUserId })
        .then(prerequisite => {
          this.reasonCode = prerequisite.reasonCode
          if (prerequisite.projectConfigured) {
            this.$router.replace(this.returnPath)
            return
          }
          // まだ設定されていない。確かめたことが分かるよう、確かめた時刻を添える。
          this.result = { type: 'info', text: '{time} に再確認しましたが、未設定です。設定後に再度お試しください。'.replace('{time}', this.checkedTime()) }
        })
        .catch(error => {
          const status = error && error.response ? Number(error.response.status) : 0
          if (status === 403) {
            this.result = { type: 'danger', text: '対象スタッフの設定状況を確認する権限がありません。' }
            return
          }
          this.result = { type: 'danger', text: 'プロジェクト設定状況を確認できませんでした。通信状態を確認して再度お試しください。' }
        })
        .finally(() => {
          this.loading = false
        })
    },

    /** 確かめた時刻（時:分）。 */
    checkedTime () {
      const now = new Date()
      return now.getHours() + ':' + String(now.getMinutes()).padStart(2, '0')
    }
  }
}
</script>

<style scoped>
.project-prerequisite-card {
  max-width: 920px;
  margin: 0 auto;
}

.project-prerequisite-heading__eyebrow {
  margin: 0 0 4px;
  color: rgba(255, 255, 255, .72);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .14em;
}

.project-prerequisite-heading h2 {
  margin: 0;
}

.project-prerequisite-steps {
  margin: 24px 0;
  padding: 18px 20px;
  border: 1px solid #d8e2e8;
  border-radius: 8px;
  background: #f8fafb;
}

.project-prerequisite-steps h3 {
  margin: 0 0 12px;
  font-size: 18px;
}

.project-prerequisite-steps ol {
  margin: 0;
  padding-left: 22px;
  line-height: 1.9;
}

.project-prerequisite-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.project-prerequisite-note {
  margin: 18px 0 0;
  color: #52616b;
  line-height: 1.7;
}

@media (max-width: 575.98px) {
  .project-prerequisite-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .project-prerequisite-actions .btn {
    width: 100%;
  }
}
</style>
